import { ScrollToTop } from "apps/visits/core/components/scroll-to-top";
import { useTrackPageViews } from "apps/visits/core/modules/tracking/gtm";
import { Loader } from "apps/visits/packages/ui/src/components/loader";
import { useEffect, useMemo, useState } from "react";
import { Route, BrowserRouter as Router, Switch, useHistory } from "react-router-dom";
import { Footer } from "@/core/shared/v2/footer";
import { Header } from "@/core/shared/v2/header";
import { useGetVoucherDistribution } from "../api/get-voucher-distribution";
import { DistributionNotFound } from "../components/distribution-not-found";
import { FreePackagesContent } from "../free-packages/pages";
import { SelectDistributionContent } from "../free-packages/pages/select-distribution";
import { FriendsAndFamilyContent } from "../friends-and-family/pages";
import { useCurrentOfferType } from "../hooks/use-current-offer-type";
import { PurchaseProductsContent } from "../purchase-products/pages";
import { AddressSelection } from "../purchase-products/pages/address-selection";
import { useVouchersDistributionState } from "../store/use-vouchers-distribution-state";
import type { ConfigName } from "../types/config-name";
import type { VoucherDistributionConfig } from "../types/voucher-distribution-config";
import { updateCmsTranslations } from "../utils/update-cms-translations";

const SLUG_URL_RE = /^\/pp\/([^/]+)/;

const extractSlugFromPath = (pathname: string): string | null => {
  const match = pathname.match(SLUG_URL_RE);
  const slug = match?.[1] ?? null;
  if (slug === "wybor-regionu" || slug === "region_selection") {
    return null;
  }
  return slug;
};

// Subscribe to client-side URL changes (both browser navigation and
// React-Router's `history.push`, which only triggers `pushState`, not
// `popstate`). We patch `pushState`/`replaceState` once per app lifetime
// so the root component can swap its Router when the slug segment
// changes.
const useCurrentPathname = (): string => {
  const [pathname, setPathname] = useState(() => (typeof window === "undefined" ? "/" : window.location.pathname));

  useEffect(() => {
    if (typeof window === "undefined") return;

    const sync = () => setPathname(window.location.pathname);

    window.addEventListener("popstate", sync);
    window.addEventListener("vouchers:locationchange", sync);

    const { pushState, replaceState } = window.history;
    const dispatch = () => window.dispatchEvent(new Event("vouchers:locationchange"));

    window.history.pushState = function patchedPushState(...args) {
      const result = pushState.apply(this, args);
      dispatch();
      return result;
    };
    window.history.replaceState = function patchedReplaceState(...args) {
      const result = replaceState.apply(this, args);
      dispatch();
      return result;
    };

    return () => {
      window.removeEventListener("popstate", sync);
      window.removeEventListener("vouchers:locationchange", sync);
      window.history.pushState = pushState;
      window.history.replaceState = replaceState;
    };
  }, []);

  return pathname;
};

const getConfigSegment = (
  configs: VoucherDistributionConfig[],
  configName: ConfigName,
  basenamePrefix: string,
): string | null => {
  const config = configs.find((c) => c.configName === configName);
  if (!config) return null;
  return config.path.replace(basenamePrefix, "") || null;
};

const VouchersDistributionContent = () => {
  const history = useHistory();
  const offerType = useCurrentOfferType();
  const { voucherDistribution } = useVouchersDistributionState();

  useTrackPageViews({
    additionalData: {
      ...(voucherDistribution && { partner_name: voucherDistribution.name }),
      ...(offerType && { offer_type: offerType }),
      ...(voucherDistribution && { single_distribution: voucherDistribution.configs.length === 1 }),
    },
  });

  if (!voucherDistribution) return null;

  const basenamePrefix = `/pp/${voucherDistribution.slug}`;
  const freePackagesSegment = getConfigSegment(voucherDistribution.configs, "free_packages", basenamePrefix);
  const purchaseProductsSegment = getConfigSegment(voucherDistribution.configs, "purchase_products", basenamePrefix);
  const friendsAndFamilySegment = getConfigSegment(voucherDistribution.configs, "friends_and_family", basenamePrefix);

  return (
    <div className="flex min-h-screen flex-col bg-primary-foreground">
      <Header onLogoClick={() => history.push("/")} contractorLogoUrl={voucherDistribution.contractorLogoUrl} />
      <div className="flex flex-grow flex-col">
        <ScrollToTop />
        <Switch>
          {freePackagesSegment && (
            <Route path={freePackagesSegment}>
              <FreePackagesContent />
            </Route>
          )}
          {purchaseProductsSegment && (
            <Route path={purchaseProductsSegment}>
              <PurchaseProductsContent />
            </Route>
          )}
          {friendsAndFamilySegment && (
            <Route path={friendsAndFamilySegment}>
              <FriendsAndFamilyContent />
            </Route>
          )}
          <Route path="/" exact>
            <SelectDistributionContent />
          </Route>
          <Route>
            <DistributionNotFound />
          </Route>
        </Switch>
      </div>
      <Footer />
    </div>
  );
};

// Wrapped in a Router so AddressSelection hooks (useTrackPageViews, etc.)
// that depend on router context keep working when no slug context exists.
const RegionSelectionApp = () => (
  <Router>
    <AddressSelection />
  </Router>
);

export const VouchersDistribution = () => {
  const pathname = useCurrentPathname();
  const urlSlug = useMemo(() => extractSlugFromPath(pathname), [pathname]);
  const { voucherDistribution, setVoucherDistribution } = useVouchersDistributionState();

  // Only fetch over HTTP when the URL slug doesn't already match the data we
  // have in the store (either from server-injected `gon` on initial load, or
  // from a previous client-side fetch).
  const shouldFetch = Boolean(urlSlug) && voucherDistribution?.slug !== urlSlug;

  const { data, error } = useGetVoucherDistribution({
    slug: urlSlug ?? "",
    config: {
      enabled: shouldFetch,
      staleTime: Infinity,
      useErrorBoundary: false,
    },
  });

  useEffect(() => {
    if (data && data.voucherDistribution.slug === urlSlug) {
      setVoucherDistribution(data.voucherDistribution);
      updateCmsTranslations(data.cmsTranslations);
    }
  }, [data, urlSlug, setVoucherDistribution]);

  if (!urlSlug) {
    return <RegionSelectionApp />;
  }

  if (!voucherDistribution || voucherDistribution.slug !== urlSlug) {
    if (error) return <DistributionNotFound />;
    return <Loader />;
  }

  // `key` forces React Router to rebuild with the new basename whenever the
  // user crosses slugs via `history.push`. Without it the Router would keep
  // the old basename and fail to match routes under the new slug.
  return (
    <Router key={voucherDistribution.slug} basename={`/pp/${voucherDistribution.slug}`}>
      <VouchersDistributionContent />
    </Router>
  );
};
